/**
 * Display formatters for the admin area. Display only — the numbers they
 * receive are already rounded by src/lib/calc/pnl.ts.
 */

const CAD = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' });

const DASH = '—';

export function money(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return CAD.format(value);
}

/**
 * A money figure short enough to sit beside a chart's axis: $0, $25k, $1.2M.
 *
 * Axis ticks are round numbers chosen by `niceTicks`, and printing one at full
 * precision costs more width than the scale it labels is worth — an axis of
 * "$25,000.00" pushes the plot into a corner. This lives here, next to
 * `money`, rather than inside a chart component, because the rule those
 * components follow is that they never invent formatting: they render a string
 * the caller hands them, and every figure on a screen is formatted in one
 * place.
 *
 * The minus is U+2212, matching the signed amounts in the ledger rather than a
 * hyphen.
 */
export function moneyAxis(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  const sign = value < 0 ? '−' : '';
  const size = Math.abs(value);
  if (size >= 1_000_000) return `${sign}$${short(size / 1_000_000)}M`;
  if (size >= 1_000) return `${sign}$${short(size / 1_000)}k`;
  return `${sign}$${Math.round(size)}`;
}

/** At most one decimal, and no trailing `.0` — `25`, not `25.0`. */
function short(value: number): string {
  return (Math.round(value * 10) / 10).toString();
}

/**
 * A margin, as a percent to one decimal.
 *
 * Nothing is a dash, never 0%. A job with no revenue has no margin to be a
 * percentage OF, and "0%" there reads as "broke even" — a claim about the job
 * rather than about the absence of a figure. Same rule the calc layer follows
 * (see numbers.ts): null means unanswerable, 0 means answered with zero.
 */
export function percent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return `${value.toFixed(1)}%`;
}

function fixed(value: number | null | undefined, unit: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return `${value.toFixed(2)} ${unit}`;
}

export const hours = (value: number | null | undefined) => fixed(value, 'h');
export const tonnes = (value: number | null | undefined) => fixed(value, 't');
export const cubes = (value: number | null | undefined) => fixed(value, 'm³');
